import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { 
  FireIcon, 
  ChartBarIcon, 
  CalendarIcon, 
  TrophyIcon,
  SparklesIcon,
  RocketLaunchIcon,
  HeartIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

import DietTrackerIcon from '../assets/Diet Tracker Default.png';
import DumbbellIcon from '../assets/Dumbbell Default.png';
import AICoachIcon from '../assets/AI Coach Default.png';

export default function Welcome({ user }) {
  const [stats, setStats] = useState({
    totalWorkouts: 0,
    currentStreak: 0,
    totalCaloriesBurned: 0,
    weeklyWorkouts: 0,
    hasProfile: false,
    recentWorkout: null,
    nextMilestone: null
  });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentWeight, setCurrentWeight] = useState(null);
  const [targetWeight, setTargetWeight] = useState(null);
  const [startWeight, setStartWeight] = useState(null);
  const [userName, setUserName] = useState('');

  const fetchDashboardData = useCallback(async () => {
    try {
      // Check if user has completed profile
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const hasProfile = profileData && profileData.gender && profileData.date_of_birth;
      
      if (profileData?.target_weight) {
        setTargetWeight(profileData.target_weight);
      }
      
      if (profileData?.name) {
        setUserName(profileData.name);
      }

      // Get latest weight measurement
      const { data: latestMeasurement } = await supabase
        .from('body_measurements')
        .select('weight')
        .eq('user_id', user.id)
        .order('measurement_date', { ascending: false })
        .limit(1)
        .single();

      if (latestMeasurement?.weight) {
        setCurrentWeight(latestMeasurement.weight);
      }

      // Get starting weight (earliest measurement)
      const { data: earliestMeasurement } = await supabase
        .from('body_measurements')
        .select('weight')
        .eq('user_id', user.id)
        .order('measurement_date', { ascending: true })
        .limit(1)
        .single();

      if (earliestMeasurement?.weight) {
        setStartWeight(earliestMeasurement.weight);
      }

      // Get all completed workouts
      const { data: allCompletedWorkouts } = await supabase
        .from('workouts')
        .select('date, exercise, completed_date')
        .eq('user_id', user.id)
        .eq('completed', true)
        .order('date', { ascending: false });

      // Count total completed exercises
      const totalWorkouts = allCompletedWorkouts?.length || 0;

      // Get exercises from last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
      
      const weeklyWorkouts = allCompletedWorkouts?.filter(w => w.date >= sevenDaysAgoStr).length || 0;

      // Get unique workout dates for streak calculation
      const uniqueDates = new Set(allCompletedWorkouts?.map(w => w.date) || []);

      // Get most recent workout info
      let recentWorkout = null;
      if (allCompletedWorkouts && allCompletedWorkouts.length > 0) {
        const mostRecentDate = allCompletedWorkouts[0].date;
        const exercisesOnDate = allCompletedWorkouts.filter(w => w.date === mostRecentDate);
        
        recentWorkout = {
          workout_date: mostRecentDate,
          workout_type: `${exercisesOnDate.length} exercises`,
          exercises: exercisesOnDate.map(w => w.exercise)
        };
      }

      // Calculate current streak
      let streak = 0;
      if (uniqueDates.size > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const sortedDates = Array.from(uniqueDates)
          .map(dateStr => {
            const d = new Date(dateStr);
            d.setHours(0, 0, 0, 0);
            return d.getTime();
          })
          .sort((a, b) => b - a); // Sort descending

        let checkDate = today.getTime();
        const oneDayMs = 24 * 60 * 60 * 1000;

        // Allow for today or yesterday as starting point
        if (!sortedDates.includes(checkDate)) {
          checkDate -= oneDayMs;
        }

        while (sortedDates.includes(checkDate)) {
          streak++;
          checkDate -= oneDayMs;
        }
      }

      // Calculate total calories (rough estimate - about 50 calories per exercise)
      const totalCaloriesBurned = totalWorkouts * 50; // Average calories per exercise

      // Determine next milestone (based on exercises)
      let nextMilestone = null;
      if (totalWorkouts < 25) nextMilestone = { count: 25, label: 'First 25 Exercises' };
      else if (totalWorkouts < 50) nextMilestone = { count: 50, label: '50 Exercise Milestone' };
      else if (totalWorkouts < 100) nextMilestone = { count: 100, label: '100 Exercise Achievement' };
      else if (totalWorkouts < 250) nextMilestone = { count: 250, label: '250 Exercise Champion' };
      else if (totalWorkouts < 500) nextMilestone = { count: 500, label: '500 Exercise Legend' };

      setStats({
        totalWorkouts: totalWorkouts || 0,
        currentStreak: streak,
        totalCaloriesBurned,
        weeklyWorkouts: weeklyWorkouts || 0,
        hasProfile,
        recentWorkout,
        nextMilestone
      });

      // Show onboarding if new user
      if (!hasProfile || totalWorkouts === 0) {
        setShowOnboarding(true);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchDashboardData();
    
    // Listen for profile updates to refresh the user's name
    const handleProfileUpdate = () => {
      fetchDashboardData();
    };
    window.addEventListener('profile-updated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, [fetchDashboardData]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getMotivationalQuote = () => {
    const quotes = [
      "The only bad workout is the one that didn't happen.",
      "Your body can stand almost anything. It's your mind you have to convince.",
      "Success is the sum of small efforts repeated day in and day out.",
      "The pain you feel today will be the strength you feel tomorrow.",
      "Don't stop when you're tired. Stop when you're done.",
      "Strive for progress, not perfection.",
      "Your health is an investment, not an expense."
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {getGreeting()}, {userName || user.email.split('@')[0]}! 
            </h1>
            <p className="text-purple-100 text-lg">
              {getMotivationalQuote()}
            </p>
          </div>
          <div className="hidden md:block">
            <RocketLaunchIcon className="h-20 w-20 text-purple-200 opacity-50" />
          </div>
        </div>
      </div>

      {/* Onboarding Banner */}
      {showOnboarding && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white shadow-xl border-4 border-blue-300">
          <div className="flex items-start space-x-4">
            <SparklesIcon className="h-12 w-12 text-yellow-300 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-3">Welcome to The Iron Path!</h2>
              <p className="text-blue-100 mb-4 text-lg">
                {!stats.hasProfile 
                  ? "Let's get started! First, complete your profile in Profile & Stats to personalize your experience."
                  : stats.totalWorkouts === 0
                  ? "You're all set up! Ready to log your first workout? Click 'Start First Workout' below to begin your fitness journey!"
                  : "Great to have you back! Keep up the amazing work!"
                }
              </p>
              <div className="flex flex-wrap gap-3">
                {!stats.hasProfile && (
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'profile' }))}
                    className="bg-yellow-500 text-blue-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-200 border-2 border-yellow-300"
                  >
                    Complete Profile
                  </button>
                )}
                {stats.hasProfile && stats.totalWorkouts === 0 && (
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'gym' }))}
                    className="bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-all duration-200 border-2 border-blue-300"
                  >
                    Start First Workout
                  </button>
                )}
                <button
                  onClick={() => setShowOnboarding(false)}
                  className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-all duration-200"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Workouts */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <ChartBarIcon className="h-8 w-8 text-purple-400" />
            <span className="text-3xl font-bold text-white">{stats.totalWorkouts}</span>
          </div>
          <p className="text-purple-200 font-semibold">Total Exercises</p>
          <p className="text-purple-300 text-sm mt-1">
            {stats.totalWorkouts === 0 ? 'Start your journey!' : 'Exercises completed!'}
          </p>
        </div>

        {/* Current Streak */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-orange-500/20 hover:border-orange-500/40 transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <FireIcon className="h-8 w-8 text-orange-400" />
            <span className="text-3xl font-bold text-white">{stats.currentStreak}</span>
          </div>
          <p className="text-orange-200 font-semibold">Day Streak</p>
          <p className="text-orange-300 text-sm mt-1">
            {stats.currentStreak === 0 ? 'Start your streak today!' : 'On fire! 🔥'}
          </p>
        </div>

        {/* Weekly Workouts */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-green-500/20 hover:border-green-500/40 transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <CalendarIcon className="h-8 w-8 text-green-400" />
            <span className="text-3xl font-bold text-white">{stats.weeklyWorkouts}</span>
          </div>
          <p className="text-green-200 font-semibold">This Week</p>
          <p className="text-green-300 text-sm mt-1">
            {stats.weeklyWorkouts === 0 ? 'Get moving!' : `${stats.weeklyWorkouts} exercises completed`}
          </p>
        </div>

        {/* Calories Burned */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-pink-500/20 hover:border-pink-500/40 transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <BoltIcon className="h-8 w-8 text-pink-400" />
            <span className="text-3xl font-bold text-white">{stats.totalCaloriesBurned.toLocaleString()}</span>
          </div>
          <p className="text-pink-200 font-semibold">Calories Burned</p>
          <p className="text-pink-300 text-sm mt-1">Estimated total</p>
        </div>
      </div>

      {/* Progress Tracker */}
      {currentWeight && targetWeight && startWeight && (
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-purple-500/20">
          <div className="flex items-center space-x-3 mb-4">
            <TrophyIcon className="h-6 w-6 text-yellow-400" />
            <h2 className="text-xl font-bold text-white">Weight Progress</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-purple-200">
              <span>Current: {currentWeight} kg</span>
              <span>Target: {targetWeight} kg</span>
            </div>
            <div className="w-full bg-purple-900/30 rounded-full h-4">
              <div 
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-4 rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(100, Math.max(0, (Math.abs(currentWeight - startWeight) / Math.abs(targetWeight - startWeight)) * 100))}%` 
                }}
              ></div>
            </div>
            <p className="text-purple-300 text-sm">
              {currentWeight > targetWeight 
                ? `${(currentWeight - targetWeight).toFixed(1)} kg to go!`
                : currentWeight < targetWeight
                ? `${(targetWeight - currentWeight).toFixed(1)} kg to go!`
                : 'Goal achieved! 🎉'
              }
            </p>
          </div>
        </div>
      )}

      {/* Next Milestone */}
      {stats.nextMilestone && (
        <div className="bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl p-6 text-white shadow-xl">
          <div className="flex items-center space-x-3 mb-3">
            <TrophyIcon className="h-8 w-8" />
            <h2 className="text-xl font-bold">Next Milestone</h2>
          </div>
          <p className="text-2xl font-bold mb-2">{stats.nextMilestone.label}</p>
          <div className="bg-white/20 rounded-full h-3 mb-2">
            <div 
              className="bg-white h-3 rounded-full transition-all duration-500"
              style={{ width: `${(stats.totalWorkouts / stats.nextMilestone.count) * 100}%` }}
            ></div>
          </div>
          <p className="text-yellow-100">
            {stats.nextMilestone.count - stats.totalWorkouts} more exercises to unlock!
          </p>
        </div>
      )}

      {/* Recent Activity */}
      {stats.recentWorkout && (
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-purple-500/20">
          <div className="flex items-center space-x-3 mb-4">
            <HeartIcon className="h-6 w-6 text-red-400" />
            <h2 className="text-xl font-bold text-white">Recent Activity</h2>
          </div>
          <div className="space-y-2">
            <p className="text-purple-200">
              <span className="font-semibold text-white">Last Workout:</span> {stats.recentWorkout.workout_type}
            </p>
            <p className="text-purple-300 text-sm">
              {new Date(stats.recentWorkout.workout_date).toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            {stats.recentWorkout.exercises && (
              <p className="text-purple-300 text-sm">
                Exercises: {stats.recentWorkout.exercises.join(', ')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'gym' }))}
          className="bg-gradient-to-r from-purple-800/80 to-purple-900/80 hover:from-purple-700/90 hover:to-purple-800/90 text-white p-6 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 border border-purple-700/50"
        >
          <img src={DumbbellIcon} alt="Dumbbell" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
          <div className="text-lg">Start Workout</div>
          <div className="text-purple-200 text-sm mt-1">Track your exercises</div>
        </button>

        <button
          onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'diet' }))}
          className="bg-gradient-to-r from-green-800/80 to-green-900/80 hover:from-green-700/90 hover:to-green-800/90 text-white p-6 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 border border-green-700/50"
        >
          <img src={DietTrackerIcon} alt="Diet" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
          <div className="text-lg">Log Meals</div>
          <div className="text-green-200 text-sm mt-1">Track your nutrition</div>
        </button>

        <button
          onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'coach' }))}
          className="bg-gradient-to-r from-blue-800/80 to-blue-900/80 hover:from-blue-700/90 hover:to-blue-800/90 text-white p-6 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 border border-blue-700/50"
        >
          <img src={AICoachIcon} alt="Coach" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
          <div className="text-lg">Ask AI Coach</div>
          <div className="text-blue-200 text-sm mt-1">Get personalized advice</div>
        </button>
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-xl">
        <h3 className="text-lg font-bold mb-3">Quick Tip</h3>
        <p className="text-indigo-100">
          Consistency is key! Try to maintain at least a 3-day workout streak each week. 
          Small, consistent efforts lead to massive results over time.
        </p>
      </div>
    </div>
  );
}